/**
 * Where the Train destination gets training history from.
 *
 * `AppPorts` (P05) registers the *platform* adapters — clock, ticker, storage
 * events, export. It declares no storage ports at all, so there is currently no
 * composition root that hands a surface an `EffortStorePort`. Rather than reach
 * into IndexedDB from a component, this module keeps the same shape the port
 * registry uses: one context carrying the three effort-side ports, replaced
 * wholesale in a test by a provider holding `@ff/domain`'s fakes.
 *
 * The default is the real `@ff/store` implementation, built once and lazily.
 * That differs from `usePorts()`, which refuses to invent a default, and the
 * difference is deliberate: a missing clock adapter has several plausible
 * implementations and picking one silently would hide a composition bug, whereas
 * there is exactly one authorized effort store in this workspace and
 * constructing a second connection to it is the failure worth preventing. The
 * lazy singleton is what prevents that second connection.
 *
 * When P05 publishes the storage ports through `AppPorts`, this file becomes a
 * one-line projection of them and the singleton goes away.
 */

import type { ReactNode } from "react";
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { EffortSessionFields, SessionReflection } from "@ff/domain";
import type {
  CheckpointStorePort,
  EffortStorePort,
  ReflectionStorePort,
  TickerPort,
} from "@ff/domain/ports";
import { createCheckpointStore, createEffortStore, createReflectionStore } from "@ff/store";
import { browserRecordDatabase } from "../../../app-state";
import { useTicker } from "../../../app-state/ports";
import { mergeEffortHistory } from "./readModel";

export interface TrainingStores {
  readonly effort: EffortStorePort;
  readonly reflections: ReflectionStorePort;
  readonly checkpoints: CheckpointStorePort;
}

const TrainingStoresContext = createContext<TrainingStores | null>(null);

/**
 * Built at most once per document. The checkpoint store needs a ticker for its
 * coalescing window, so the singleton is keyed on nothing else — a second bundle
 * would mean a second coalescing window, and two windows racing to write the
 * same singleton checkpoint row is exactly the corruption the verified-write
 * path exists to make impossible.
 */
let defaultStores: TrainingStores | undefined;

function browserTrainingStores(ticker: TickerPort): TrainingStores {
  if (defaultStores === undefined) {
    const database = browserRecordDatabase();
    defaultStores = {
      effort: createEffortStore(database),
      reflections: createReflectionStore(database),
      checkpoints: createCheckpointStore(database, ticker),
    };
  }
  return defaultStores;
}

export interface TrainingStoresProviderProps {
  readonly stores: TrainingStores;
  readonly children?: ReactNode;
}

export function TrainingStoresProvider({
  stores,
  children,
}: TrainingStoresProviderProps): ReactNode {
  return (
    <TrainingStoresContext.Provider value={stores}>{children}</TrainingStoresContext.Provider>
  );
}

export function useTrainingStores(): TrainingStores {
  const provided = useContext(TrainingStoresContext);
  const ticker = useTicker();
  return provided ?? browserTrainingStores(ticker);
}

/**
 * What the Train surface knows about saved training.
 *
 * `isUnreadable` is a first-class field rather than an empty list, because those
 * two states have opposite meanings to the person reading the screen: one says
 * "you have not trained yet" and the other says "your record is here but cannot
 * be read". Collapsing them is how a storage fault gets shown as data loss.
 */
export interface TrainingHistory {
  readonly isLoaded: boolean;
  readonly isUnreadable: boolean;
  /** Merged canonical + legacy, newest first (F-HS-27). */
  readonly sessions: readonly EffortSessionFields[];
  readonly reflections: ReadonlyMap<string, SessionReflection>;
  readonly refresh: () => void;
}

const EMPTY_REFLECTIONS: ReadonlyMap<string, SessionReflection> = new Map();

interface HistoryState {
  readonly isLoaded: boolean;
  readonly isUnreadable: boolean;
  readonly sessions: readonly EffortSessionFields[];
  readonly reflections: ReadonlyMap<string, SessionReflection>;
}

const INITIAL_STATE: HistoryState = {
  isLoaded: false,
  isUnreadable: false,
  sessions: [],
  reflections: EMPTY_REFLECTIONS,
};

export function useTrainingHistory(): TrainingHistory {
  const stores = useTrainingStores();
  const [state, setState] = useState<HistoryState>(INITIAL_STATE);
  const [generation, setGeneration] = useState(0);

  useEffect(() => {
    // A reload that resolves after the component is gone must not call
    // `setState`, and a *stale* reload that resolves after a newer one must not
    // overwrite it. One flag closes both, because both are the same bug.
    let current = true;

    void (async () => {
      const [canonical, legacy, reflections] = await Promise.all([
        stores.effort.readSessions(),
        stores.effort.readLegacyRecords(),
        stores.reflections.read(),
      ]);
      if (!current) return;

      const unreadable =
        canonical.status === "unreadable" ||
        legacy.status === "unreadable" ||
        reflections.status === "unreadable";

      const sessions = mergeEffortHistory(
        canonical.status === "loaded" ? canonical.value : [],
        legacy.status === "loaded" ? legacy.value : [],
      );

      const byId = new Map<string, SessionReflection>();
      if (reflections.status === "loaded") {
        for (const reflection of reflections.value) byId.set(reflection.sessionId, reflection);
      }

      setState({
        isLoaded: true,
        isUnreadable: unreadable,
        // Fail closed: an unreadable store contributes nothing, and the surface
        // says so through `isUnreadable` rather than by rendering a short list
        // that looks like the whole record.
        sessions: unreadable ? [] : sessions,
        reflections: unreadable ? EMPTY_REFLECTIONS : byId,
      });
    })();

    return () => {
      current = false;
    };
  }, [stores, generation]);

  const refresh = useCallback(() => {
    setGeneration((value) => value + 1);
  }, []);

  return useMemo(
    () => ({
      isLoaded: state.isLoaded,
      isUnreadable: state.isUnreadable,
      sessions: state.sessions,
      reflections: state.reflections,
      refresh,
    }),
    [state, refresh],
  );
}
